import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import propTypes from 'prop-types';
import { ActionBar } from './action-bar/ActionBar';
import { StickyActionBar } from './action-bar/StickyActionBar';
import { ActionBarIntersectionObserver } from './action-bar/ActionBarIntersectionObserver';
import { GroupAccordionContext } from './GroupAccordionContext';
import { GroupWrapper } from './group-wrapper/GroupWrapper';
import {getGroupToExpand, mapIds} from '../utils';

const { arrayOf, bool, func, number, shape, string, oneOfType, oneOf } = propTypes

const GroupAccordion = forwardRef(({
  renderActionButtons,
  stickyRenderActionButtons,
  onExpandAll,
  onCollapseAll,
  groupedItems,
  renderPod,
  onDragEnd,
  showNumbering,
  placeHolderConfig,
  renderGroupButtons,
  onSelect,
  onSelectAllGroupsAndItems,
  disableStickyActionBar,
  stickyActionBarToBottom,
  onGroupExpand,
  disableSelection,
  disableDragAndDrop,
  renderSubtitle,
  renderProductLoader,
  isLoading,
  groupToBeAutoOpened,
  mobileActionBar,
  userSelect,
  showHorizontalDragHandleContainer,
  renderDeleteButton,
  errorSelectionMessage = null,
  hideSelectAll,
  defaultSelectedItems: defaultSelectedItems = {},
  noBorders = true,
  disablePlaceholders = false
}, ref) => {
  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('group-accordion.ready');
  }, []);
  const firstLoaded = useRef(false);

  const [isIntersecting, setIntersecting] = useState(true);
  const [expandedItems, setExpandedItems] = useState(getGroupToExpand(groupedItems));
  const [selectedItems, setSelectedItems] = useState(defaultSelectedItems);

  useEffect(() => {
    if (!firstLoaded.current && groupedItems?.length) {
      firstLoaded.current = true;
      const groupToExpand = getGroupToExpand(groupedItems);
      if (groupToExpand?.length) {
        setExpandedItems(groupToExpand);
      }
    }
  }, [groupedItems]);

  const onSelectItem = (selectedItemId, groupId) => {
    if (!selectedItemId || !groupId) return;
    const updateSelected = (previouSelectedItems) => {
      const lengthOfItemsInGroup = groupedItems.find(
        (group) => group.id === groupId
      ).items.length;
      const isSelected = Boolean(previouSelectedItems[groupId]?.isSelected);
      const items = [
        ...(previouSelectedItems[groupId]?.items || []),
        selectedItemId,
      ];
      const isIndeterminate = Boolean(
        isSelected && items.length !== lengthOfItemsInGroup
      );
      return {
        ...previouSelectedItems,
        [groupId]: {
          items,
          isSelected,
          isIndeterminate,
        },
      };
    };

    const updatedSelectedItems = updateSelected(selectedItems);
    setSelectedItems(updatedSelectedItems);

    onSelect({
      action: 'select',
      target: 'item',
      data: [
        {
          groupId,
          items: [selectedItemId],
        },
      ],
      selectedItems: updatedSelectedItems,
    });
  };

  const onSelectAll = () => {
    const updatedSelectedItems = {};
    groupedItems.forEach((group) => {
      updatedSelectedItems[group.id] = {
        isSelected: !group.isDefault,
        items: [...mapIds(group?.items), ...mapIds(group?.notLoadedItems)],
      };
    });
    setSelectedItems(updatedSelectedItems);

    onSelect({
      action: 'select',
      target: 'all',
      data: groupedItems.map(({ id, items }) => ({ groupId: id, items: mapIds(items) })),
      selectedItems: updatedSelectedItems,
    });
  };

  const onSelectGroup = (groupId) => {
    if (!groupId) return;
    const updateSelected = (previouSelectedItems) => {
      const selectedGroup = groupedItems.find((group) => group.id === groupId);
      return {
        ...previouSelectedItems,
        [groupId]: {
          items: [...mapIds(selectedGroup.items), ...mapIds(selectedGroup?.notLoadedItems)],
          isSelected: true,
          isIndeterminate: false,
        },
      };
    };

    const updatedSelectedItems = updateSelected(selectedItems);
    setSelectedItems(updatedSelectedItems);

    onSelect({
      action: 'select',
      target: 'group',
      data: [
        {
          groupId,
          items: groupedItems
            .find(({ id }) => id === groupId)?.items ?? [],
        },
      ],
      selectedItems: updatedSelectedItems,
    });
  };

  const onUnselectItem = (unSelectedItem, groupId) => {
    if (!unSelectedItem || !groupId) return;
    if (
      !selectedItems[groupId].isSelected
      && selectedItems[groupId].items.length <= 1
    ) {
      onUnselectGroup(groupId);
      return;
    }
    const updateSelected = (previouSelectedItems) => {
      const isSelected = Boolean(previouSelectedItems[groupId]?.isSelected);
      const items = previouSelectedItems[groupId]?.items.filter(
        (item) => item !== unSelectedItem
      );
      return {
        ...previouSelectedItems,
        [groupId]: {
          items,
          isSelected,
          isIndeterminate: isSelected,
        },
      };
    };

    const updatedSelectedItems = updateSelected(selectedItems);
    setSelectedItems(updateSelected);

    onSelect({
      action: 'unselect',
      target: 'item',
      data: [
        {
          groupId,
          items: [unSelectedItem],
        },
      ],
      selectedItems: updatedSelectedItems
    });
  };

  const onUnselectGroup = (unselectedGroupId) => {
    if (!unselectedGroupId) return;
    const updateSelected = (previouSelectedItems) => {
      const updatedSelectedItems = {};
      Object.keys(previouSelectedItems).forEach((groupId) => {
        if (groupId !== unselectedGroupId) {
          updatedSelectedItems[groupId] = {
            items: [...previouSelectedItems[groupId]?.items],
            isSelected: Boolean(previouSelectedItems[groupId]?.isSelected),
            isIndeterminate: Boolean(
              previouSelectedItems[groupId]?.isIndeterminate
            ),
          };
        }
      });
      return updatedSelectedItems;
    };

    const updatedSelectedItems = updateSelected(selectedItems);
    setSelectedItems(updatedSelectedItems);

    onSelect({
      action: 'unselect',
      target: 'group',
      data: [
        {
          groupId: unselectedGroupId,
          items: mapIds(groupedItems
            .find(({ id }) => id === unselectedGroupId)?.items) ?? [],
        },
      ],
      selectedItems: updatedSelectedItems,
    });
  };
  const onUnselectAll = () => {
    setSelectedItems({});

    onSelect({
      action: 'unselect',
      target: 'all',
      data: groupedItems.map(({ id, items }) => ({ groupId: id, items: mapIds(items) })),
      selectedItems: {}
    });
  };

  const context = {
    data: groupedItems,
    selectedItems,
    onExpandAll,
    expandedItems,
    disableSelection,
    disableDragAndDrop,
    showHorizontalDragHandleContainer,
    setExpandedItems,
    onSelectItem,
    onUnselectItem,
    onUnselectAll,
    onUnselectGroup,
    onSelectAll,
    onSelectGroup,
  };

  useImperativeHandle(ref, () => ({
    selectAll: onSelectAll,
  }), [groupedItems]);

  useEffect(() => {
    const updatedSelectedItems = {};
    groupedItems.forEach((group) => {
      const itemIds = mapIds(group?.items);
      const notLoadedItems = mapIds(group?.notLoadedItems);
      if (!selectedItems[group.id]) return;
      // newly loaded or added items in a fully selected group should be added to selected items
      if (
        selectedItems[group.id]?.isSelected
        && !selectedItems[group.id]?.isIndeterminate
      ) {
        updatedSelectedItems[group.id] = {
          ...selectedItems[group.id],
          items: [...itemIds, ...notLoadedItems],
        };
      } else {
        // remove any selected items that no longer exist in groupedItems
        updatedSelectedItems[group.id] = {
          ...selectedItems[group.id],
          items: [...selectedItems[group.id].items].filter((item) => {
            return itemIds.includes(item) || notLoadedItems.includes(item);
          }),
        };
      }
      // remove any empty groups that are not selected
      if (updatedSelectedItems[group.id].items.length === 0 && !updatedSelectedItems[group.id].isSelected) {
        delete updatedSelectedItems[group.id];
      }
    });
    setSelectedItems(updatedSelectedItems);
  }, [groupedItems]);

  return (
    <div>
      <GroupAccordionContext.Provider value={context}>
        <ActionBarIntersectionObserver setIntersecting={setIntersecting}>
          <ActionBar
            renderActionButtons={renderActionButtons}
            onExpandAll={onExpandAll}
            onCollapseAll={onCollapseAll}
            mobileActionBar={mobileActionBar}
            onSelectAllGroupsAndItems={onSelectAllGroupsAndItems}
            errorSelectionMessage={errorSelectionMessage}
            hideSelectAll={hideSelectAll}
          />
        </ActionBarIntersectionObserver>
        <div data-component="GroupAccordion">
          <GroupWrapper
            renderGroupButtons={renderGroupButtons}
            renderPod={renderPod}
            onDragEnd={onDragEnd}
            showNumbering={showNumbering}
            placeHolderConfig={placeHolderConfig}
            onGroupExpand={onGroupExpand}
            renderSubtitle={renderSubtitle}
            renderProductLoader={renderProductLoader}
            isLoading={isLoading}
            groupToBeAutoOpened={groupToBeAutoOpened}
            userSelect={userSelect}
            onSelectAllGroupsAndItems={onSelectAllGroupsAndItems}
            showHorizontalDragHandleContainer={showHorizontalDragHandleContainer}
            renderDeleteButton={renderDeleteButton}
            noBorders={noBorders}
            disablePlaceholders={disablePlaceholders}
          />
        </div>
        {!isIntersecting && (disableStickyActionBar !== true) && (
          <StickyActionBar
            stickyRenderActionButtons={stickyRenderActionButtons}
            stickyActionBarToBottom={stickyActionBarToBottom}
            onSelectAllGroupsAndItems={onSelectAllGroupsAndItems}
            onExpandAll={onExpandAll}
            onCollapseAll={onCollapseAll}
            errorSelectionMessage={errorSelectionMessage}
          />
        )}
      </GroupAccordionContext.Provider>
    </div>
  );
});

GroupAccordion.propTypes = {
  groupedItems: arrayOf(
    shape({
      id: string.isRequired,
      noOfItems: number.isRequired,
      title: string.isRequired,
      subtitle: string,
      items: arrayOf(oneOfType([string, shape({
        id: string.isRequired,
        alert: oneOfType([
          shape({
            status: oneOf(['success', 'warning', 'error', 'info']),
            render: func,
          }),
          func,
        ]),
      })])).isRequired,
      notLoadedItems: arrayOf(oneOfType([string, shape({
        id: string.isRequired,
        alert: oneOfType([
          shape({
            status: oneOf(['success', 'warning', 'error', 'info']),
            render: func,
          }),
          func,
        ]),
      })])),
      needByDate: string,
      isDefault: bool
    })
  ),
  renderPod: func.isRequired,
  renderGroupButtons: func,
  renderActionButtons: func,
  stickyRenderActionButtons: func,
  renderSubtitle: func,
  onDragEnd: func,
  onExpandAll: func,
  onCollapseAll: func,
  showNumbering: bool,
  placeHolderConfig: shape({
    height: number,
    numberOfLines: number,
    numberOfPlaceHolders: number
  }),
  onSelect: func,
  onSelectAllGroupsAndItems: func,
  disableStickyActionBar: bool,
  stickyActionBarToBottom: bool,
  onGroupExpand: func,
  disableSelection: bool,
  disableDragAndDrop: bool,
  renderProductLoader: func,
  isLoading: bool,
  groupToBeAutoOpened: string,
  mobileActionBar: bool,
  userSelect: string,
  showHorizontalDragHandleContainer: bool,
  renderDeleteButton: func,
  errorSelectionMessage: string,
  hideSelectAll: bool,
  disablePlaceholders: bool,
};

GroupAccordion.defaultProps = {
  renderActionButtons: () => {},
  stickyRenderActionButtons: () => {},
  onExpandAll: () => {},
  onCollapseAll: () => {},
  onGroupExpand: () => {},
  onDragEnd: () => {},
  renderGroupButtons: null,
  groupedItems: [],
  showNumbering: false,
  placeHolderConfig: {
    height: 32,
    numberOfLines: 3,
    numberOfPlaceHolders: 6,
  },
  onSelect: () => {},
  onSelectAllGroupsAndItems: () => {},
  disableStickyActionBar: false,
  stickyActionBarToBottom: false,
  disableSelection: false,
  disableDragAndDrop: false,
  renderSubtitle: null,
  renderProductLoader: null,
  isLoading: false,
  groupToBeAutoOpened: '',
  mobileActionBar: false,
  userSelect: 'none',
  showHorizontalDragHandleContainer: false,
  renderDeleteButton: () => {},
  errorSelectionMessage: null,
  hideSelectAll: false,
  disablePlaceholders: false
};

GroupAccordion.displayName = 'GroupAccordion';

export { GroupAccordion };
